import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { COLORS } from "../assets/theme";
import CustomDialog from "./common/CustomDialog";
import CustomButton from "./common/CustomButton";
import { Team, TeamUser, useTeamsQuery } from "../services/UsersService";

interface ITeamsDialogProps {
  open: boolean;
  onClose: () => void;
}

interface TreeNode {
  id: string;
  name: string;
  email: string;
  enabled?: boolean;
  directCount?: number;
  totalCount?: number;
  children: TreeNode[];
}

const UNASSIGNED_ID = "__unassigned__";

const CONNECTOR = `1.5px solid ${COLORS.mutedSurfaceBorder}`;
const ARM = "18px";
const CARD_WIDTH = 150;
const STACK_GUTTER = 16;
const STACK_FROM = 3;
const STACK_WIDTH = (STACK_GUTTER + CARD_WIDTH) * 2;
const MIN_SCALE = 0.45;
const treeSx = {
  "& ul": {
    display: "flex",
    justifyContent: "center",
    listStyle: "none",
    margin: 0,
    padding: 0,
    paddingTop: ARM,
    position: "relative",
    flexShrink: 0,
  },
  "& li": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    listStyle: "none",
    position: "relative",
    padding: `${ARM} 4px 0 4px`,
    flexShrink: 0,
  },
  "& li::before, & li::after": {
    content: '""',
    position: "absolute",
    top: 0,
    right: "50%",
    width: "50%",
    height: ARM,
    borderTop: CONNECTOR,
  },
  "& li::after": {
    right: "auto",
    left: "50%",
    borderLeft: CONNECTOR,
  },
  "& li:only-of-type": { paddingTop: 0 },
  "& li:only-of-type::before, & li:only-of-type::after": { display: "none" },
  "& li:first-of-type::before, & li:last-of-type::after": { border: 0 },
  "& li:first-of-type::after": { borderRadius: "6px 0 0 0" },
  "& li:last-of-type::before": {
    borderRight: CONNECTOR,
    borderRadius: "0 6px 0 0",
  },
  "& ul ul::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "50%",
    width: 0,
    height: ARM,
    borderLeft: CONNECTOR,
  },
  "& > ul": { paddingTop: 0 },
  "& > ul > li": { paddingTop: 0 },
  "& > ul > li::before, & > ul > li::after": { display: "none" },

  "& ul[data-stack]": {
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: STACK_WIDTH,
  },
  "& li[data-stack-row]": {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    width: STACK_WIDTH,
    padding: 0,
  },
  "& li[data-stack-row] > div": { marginTop: "3px", marginBottom: "3px" },
  "& li[data-stack-row]::before": {
    content: '""',
    position: "absolute",
    left: "50%",
    right: "auto",
    top: 0,
    bottom: 0,
    width: 0,
    height: "auto",
    border: 0,
    borderLeft: CONNECTOR,
    borderRadius: 0,
  },
  "& li[data-stack-row]:last-of-type::before": {
    bottom: "auto",
    height: "50%",
  },
  "& li[data-stack-row]::after": {
    content: '""',
    position: "absolute",
    left: "50%",
    right: "auto",
    top: "50%",
    bottom: "auto",
    width: STACK_GUTTER,
    height: 0,
    border: 0,
    borderTop: CONNECTOR,
    borderRadius: 0,
  },
  "& li[data-stack-holder] > ul[data-stack]": { paddingTop: 0 },
  "& li[data-stack-holder] > ul[data-stack]::before": { display: "none" },
};

const useFitToWidth = (enabled: boolean) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [fit, setFit] = useState({ scale: 1, height: 0 });

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!enabled || !container || !content) {
      return;
    }

    const measure = () => {
      const available = container.clientWidth;
      const natural = Math.max(content.scrollWidth, content.offsetWidth);
      if (available <= 0 || natural <= 0) {
        return;
      }
      const scale =
        natural <= available
          ? 1
          : Math.max(
              MIN_SCALE,
              Math.floor(((available - 2) / natural) * 100) / 100
            );
      const height = Math.round(content.offsetHeight * scale);
      setFit((prev) =>
        prev.scale === scale && prev.height === height
          ? prev
          : { scale, height }
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(content);
    return () => observer.disconnect();
  }, [enabled]);

  return { containerRef, contentRef, ...fit };
};

const CardChip: React.FC<{
  label: string;
  background: string;
  border?: string;
}> = ({ label, background, border }) => (
  <Chip
    size="small"
    label={label}
    clickable={false}
    onClick={(event) => event.stopPropagation()}
    sx={{
      height: 17,
      fontSize: "0.62rem",
      "& .MuiChip-label": { px: 0.75 },
      cursor: "default",
      backgroundColor: background,
      border: border ? `1px solid ${border}` : undefined,
      color: COLORS.typographyColor,
    }}
  />
);

const NodeCard: React.FC<{
  node: TreeNode;
  collapsed: boolean;
  onToggle: (id: string) => void;
}> = ({ node, collapsed, onToggle }) => {
  const isLeader = node.children.length > 0;
  const isInactive = node.enabled === false;

  const toggle = () => {
    if (isLeader) {
      onToggle(node.id);
    }
  };

  return (
    <Box
      onClick={toggle}
      role={isLeader ? "button" : undefined}
      tabIndex={isLeader ? 0 : undefined}
      aria-expanded={isLeader ? !collapsed : undefined}
      onKeyDown={(event) => {
        if (isLeader && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          toggle();
        }
      }}
      sx={{
        position: "relative",
        width: CARD_WIDTH,
        px: 1,
        py: 0.75,
        borderRadius: "10px",
        backgroundColor: COLORS.iconColor,
        border: `1px solid ${
          isLeader ? COLORS.goldChipBorder : COLORS.dividerColor
        }`,
        borderLeft: `3px solid ${isLeader ? COLORS.GOLD : COLORS.dividerColor}`,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
        opacity: isInactive ? 0.6 : 1,
        cursor: isLeader ? "pointer" : "default",
        userSelect: isLeader ? "none" : "auto",
        transition: "background-color 0.2s ease",
        "&:hover": isLeader
          ? { backgroundColor: COLORS.goldButtonHoverBackground }
          : undefined,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        {isLeader ? (
          <GroupsIcon sx={{ fontSize: 15, color: COLORS.GOLD }} />
        ) : (
          <PersonIcon sx={{ fontSize: 15, color: COLORS.placeHolderColor }} />
        )}
        <Typography
          noWrap
          sx={{
            fontWeight: 600,
            fontSize: "0.8rem",
            color: COLORS.typographyColor,
            minWidth: 0,
          }}
        >
          {node.name}
        </Typography>
      </Box>
      <Typography
        noWrap
        sx={{
          fontSize: "0.68rem",
          lineHeight: 1.4,
          color: COLORS.placeHolderColor,
          display: "block",
        }}
      >
        {node.email}
      </Typography>

      <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
        {isLeader && (
          <CardChip
            label={
              node.totalCount === node.directCount
                ? `${node.directCount} member${
                    node.directCount === 1 ? "" : "s"
                  }`
                : `${node.directCount} managers · ${node.totalCount} total`
            }
            background={COLORS.goldChipBackground}
            border={COLORS.goldChipBorder}
          />
        )}
        {isInactive && (
          <CardChip
            label="Inactive"
            background={COLORS.mutedSurfaceBackground}
          />
        )}
      </Box>
    </Box>
  );
};

const TreeBranch: React.FC<{
  node: TreeNode;
  collapsedIds: Set<string>;
  onToggle: (id: string) => void;
  inStack?: boolean;
}> = ({ node, collapsedIds, onToggle, inStack }) => {
  const collapsed = collapsedIds.has(node.id);
  const branches = node.children.filter((child) => child.children.length > 0);
  const leaves = node.children.filter((child) => child.children.length === 0);
  const stackLeaves = leaves.length >= STACK_FROM;
  const wholeGroupStacks = stackLeaves && branches.length === 0;

  const renderBranches = (nodes: TreeNode[], asRows: boolean) =>
    nodes.map((child) => (
      <TreeBranch
        key={child.id}
        node={child}
        collapsedIds={collapsedIds}
        onToggle={onToggle}
        inStack={asRows}
      />
    ));

  return (
    <Box component="li" {...(inStack ? { "data-stack-row": "" } : {})}>
      <NodeCard node={node} collapsed={collapsed} onToggle={onToggle} />
      {node.children.length > 0 && !collapsed && (
        <Box component="ul" {...(wholeGroupStacks ? { "data-stack": "" } : {})}>
          {wholeGroupStacks ? (
            renderBranches(node.children, true)
          ) : !stackLeaves ? (
            renderBranches(node.children, false)
          ) : (
            <>
              {renderBranches(branches, false)}
              {/* The plain members hang together off one drop of the bus. */}
              <Box component="li" data-stack-holder="">
                <Box component="ul" data-stack="">
                  {renderBranches(leaves, true)}
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

const toNode = (user: TeamUser): TreeNode => ({
  id: user.id,
  name: user.name,
  email: user.email,
  enabled: user.enabled,
  children: [],
});

const TeamsDialog: React.FC<ITeamsDialogProps> = ({ open, onClose }) => {
  const { data: teams = [], isLoading, isError, error } = useTeamsQuery(open);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  const { roots, unassignedTeam, leaderIds } = useMemo(() => {
    const leaderTeams = teams.filter((team) => !team.unassigned && team.leader);
    const teamsByLeader = new Map<string, Team>(
      leaderTeams.map((team) => [team.leader!.id, team])
    );

    const buildNode = (team: Team, ancestors: string[]): TreeNode => {
      const leaderId = team.leader!.id;
      return {
        ...toNode(team.leader!),
        directCount: team.directMemberCount,
        totalCount: team.totalMemberCount,
        children: team.directMembers.map((member) => {
          const childTeam = teamsByLeader.get(member.id);
          const nests =
            !!childTeam &&
            childTeam.parentLeaderId === leaderId &&
            !ancestors.includes(member.id);
          return nests
            ? buildNode(childTeam!, [...ancestors, leaderId])
            : toNode(member);
        }),
      };
    };

    return {
      roots: leaderTeams
        .filter(
          (team) =>
            !team.parentLeaderId || !teamsByLeader.has(team.parentLeaderId)
        )
        .map((team) => buildNode(team, [])),
      unassignedTeam: teams.find((team) => team.unassigned),
      leaderIds: leaderTeams.map((team) => team.leader!.id),
    };
  }, [teams]);

  const toggleNode = (id: string) =>
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const collapsibleIds = useMemo(
    () => (unassignedTeam ? [...leaderIds, UNASSIGNED_ID] : leaderIds),
    [leaderIds, unassignedTeam]
  );
  const allCollapsed =
    collapsibleIds.length > 0 &&
    collapsibleIds.every((id) => collapsedIds.has(id));
  const unassignedCollapsed = collapsedIds.has(UNASSIGNED_ID);

  const { containerRef, contentRef, scale, height } = useFitToWidth(
    roots.length > 0
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: COLORS.GOLD }} />
        </Box>
      );
    }

    if (isError) {
      return (
        <Typography sx={{ color: COLORS.errorRed, py: 2 }}>
          {error?.message || "An error occurred while getting the teams."}
        </Typography>
      );
    }

    if (roots.length === 0 && !unassignedTeam) {
      return (
        <Typography sx={{ color: COLORS.placeHolderColor, py: 2 }}>
          No teams yet. Set a team leader on a user to build one.
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {roots.length > 0 && (
          <Box
            ref={containerRef}
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              pb: 1,
              pt: 0.5,
            }}
          >
            <Box sx={{ height: height || undefined }}>
              <Box
                ref={contentRef}
                sx={{
                  width: "max-content",
                  marginInline: "auto",
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  ...treeSx,
                }}
              >
                <Box component="ul" sx={{ gap: 1.5 }}>
                  {roots.map((root) => (
                    <TreeBranch
                      key={root.id}
                      node={root}
                      collapsedIds={collapsedIds}
                      onToggle={toggleNode}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {unassignedTeam && (
          <Box
            sx={{
              border: `1px dashed ${COLORS.mutedSurfaceBorder}`,
              borderRadius: "12px",
              p: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Only the title itself toggles, so the count beside it is inert. */}
              <Box
                onClick={() => toggleNode(UNASSIGNED_ID)}
                role="button"
                tabIndex={0}
                aria-expanded={!unassignedCollapsed}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggleNode(UNASSIGNED_ID);
                  }
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  ml: -0.75,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: "8px",
                  cursor: "pointer",
                  userSelect: "none",
                  color: COLORS.GOLD,
                  "&:hover": {
                    textDecoration: "underline",
                    backgroundColor: COLORS.goldButtonHoverBackground,
                  },
                }}
              >
                <PersonIcon fontSize="small" />
                <Typography sx={{ fontWeight: 600, color: "inherit" }}>
                  Unassigned
                </Typography>
                <KeyboardArrowDownIcon
                  fontSize="small"
                  sx={{
                    transition: "transform 0.2s ease",
                    transform: unassignedCollapsed
                      ? "rotate(-90deg)"
                      : "rotate(0deg)",
                  }}
                />
              </Box>
              <CardChip
                label={`${unassignedTeam.directMemberCount}`}
                background={COLORS.mutedSurfaceBackground}
              />
            </Box>
            <Collapse in={!unassignedCollapsed} timeout="auto">
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1.5 }}
              >
                {unassignedTeam.directMembers.map((member) => (
                  <NodeCard
                    key={member.id}
                    node={toNode(member)}
                    collapsed={false}
                    onToggle={toggleNode}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      mode="teams"
      title="Teams"
      titleAction={
        collapsibleIds.length > 0 ? (
          <CustomButton
            size="small"
            variant="outlined"
            label={allCollapsed ? "Expand all" : "Collapse all"}
            textColor={COLORS.typographyColor}
            borderColor={COLORS.GOLD}
            borderRadius="10px"
            onClick={() =>
              setCollapsedIds(allCollapsed ? new Set() : new Set(collapsibleIds))
            }
          />
        ) : undefined
      }
      fields={[{ type: "custom", fullWidth: true, component: renderContent() }]}
      contentSx={{ maxHeight: "68vh", overflowY: "auto" }}
    />
  );
};

export default TeamsDialog;
